/**
 * Learning Paths — editorial cards with restrained progress
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpenIcon, ClockIcon } from '@heroicons/react/24/outline';
import AuraCharacter from '../common/AuraCharacter';
import HeaderNav from '../HeaderNav';
import './Learning.css';

const PAGE_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const LearningPaths = ({ user, onLogout }) => {
  const [paths, setPaths] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPathsAndProgress();
  }, []);

  const loadPathsAndProgress = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      const pathsRes = await axios.get('/api/learning/progress/paths', {
        headers: { Authorization: `Bearer ${token}` },
      });

      let pathsData = [];
      if (pathsRes.data) {
        if (pathsRes.data.paths) pathsData = pathsRes.data.paths;
        else if (Array.isArray(pathsRes.data)) pathsData = pathsRes.data;
        else if (pathsRes.data.success && pathsRes.data.paths) pathsData = pathsRes.data.paths;
      }
      setPaths(pathsData);

      if (user?.user_id) {
        const progressRes = await axios.get(
          `/api/learning/progress/user/${user.user_id}/progress`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const progressMap = {};
        (progressRes.data.progress || []).forEach((p) => {
          progressMap[p.path_id] = p;
        });
        setProgress(progressMap);
      }
    } catch (err) {
      console.error('Error loading paths:', err);
      setError('Failed to load learning paths');
    } finally {
      setLoading(false);
    }
  };

  const getPathProgress = (pathId) =>
    progress[pathId] || { progress_percentage: 0, is_complete: false, completed_lessons: [] };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading learning paths…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6">
        <div className="surface-panel p-6 max-w-md border border-danger/40">
          <p className="text-body text-danger font-medium mb-2">Error</p>
          <p className="text-body text-secondary">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      {user && (
        <HeaderNav
          activeSection="learn-and-play"
          onSectionChange={() => {}}
          user={user}
          onLogout={onLogout}
          visionCount={0}
        />
      )}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <motion.header {...PAGE_MOTION} className="text-center mb-12">
          <h1 className="font-display text-h1 text-primary mb-3">Learning paths</h1>
          <p className="text-body text-secondary">Master financial literacy at your own pace</p>
        </motion.header>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {paths?.length > 0 ? (
            paths.map((path, index) => {
              const pathProgress = getPathProgress(path.id);
              const isComplete = pathProgress.is_complete;
              const progressPct = pathProgress.progress_percentage || 0;

              return (
                <motion.button
                  key={path.id}
                  type="button"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.05 }}
                  onClick={() => navigate(`/learning/paths/${path.id}`)}
                  className="surface-panel p-6 text-left hover:bg-pine-tint/30 transition-colors duration-300 w-full"
                >
                  <div className="w-12 h-12 rounded-lg border border-hairline bg-pine-tint flex items-center justify-center mb-4">
                    <BookOpenIcon className="w-6 h-6 text-pine" />
                  </div>

                  <h2 className="font-display text-h2 text-primary mb-2">{path.name}</h2>
                  <p className="text-label text-secondary mb-5">{path.description}</p>

                  <div className="mb-4">
                    <div className="flex items-center justify-between text-label text-secondary mb-1.5">
                      <span>Progress</span>
                      <span className="figure text-primary tabular-nums">{Math.round(progressPct)}%</span>
                    </div>
                    <div className="progress-bar-track">
                      <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-label text-faint mb-4">
                    <span>{path.lesson_count} lessons</span>
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {path.estimated_time || '2–3 hours'}
                    </span>
                  </div>

                  {isComplete && <span className="badge-mark badge-mark-earned">Completed</span>}
                  {!isComplete && progressPct > 0 && (
                    <span className="badge-mark">In progress</span>
                  )}
                </motion.button>
              );
            })
          ) : (
            <p className="col-span-3 text-center text-body text-secondary py-12">
              No learning paths available. Please check back later.
            </p>
          )}
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-ghost text-label"
          >
            Back
          </button>
        </div>
      </div>

      <AuraCharacter
        context="learning"
        initialMessage="Choose a learning path to get started. Complete paths to unlock new game modes."
        position="bottom-right"
        compact={false}
      />
    </div>
  );
};

export default LearningPaths;
